import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, Mail, Check, ArrowRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection({ centres }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", centre: "" });
  const [submitState, setSubmitState] = useState("idle");

  const handleContact = async (e) => {
    e.preventDefault();
    setSubmitState("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitState("success");
      setForm({ name: "", email: "", phone: "", message: "", centre: "" });
    } catch {
      setSubmitState("error");
    }
  };

  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="text-center mb-14">
          <span className="reveal text-[#3B9DA5] text-sm font-semibold uppercase tracking-widest">
            {t.contact.subtitle}
          </span>
          <h2 className="reveal reveal-delay-1 font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mt-3">
            {t.contact.title}
          </h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <div className="reveal reveal-delay-1">
            {submitState === "success" ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="contact-success">
                <div className="w-16 h-16 gradient-bg rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <p className="text-lg font-semibold text-slate-900">{t.contact.success}</p>
                <button
                  onClick={() => setSubmitState("idle")}
                  className="text-[#2E4DB5] text-sm font-medium underline"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t.contact.name} *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t.contact.name}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4DB5]/30 focus:border-[#2E4DB5] transition-colors"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t.contact.email} *
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t.contact.email}
                      className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4DB5]/30 focus:border-[#2E4DB5] transition-colors"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t.contact.phone}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t.contact.phone}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4DB5]/30 focus:border-[#2E4DB5] transition-colors"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t.contact.centre_label}
                  </label>
                  <select
                    value={form.centre}
                    onChange={(e) => setForm({ ...form, centre: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4DB5]/30 focus:border-[#2E4DB5] transition-colors bg-white"
                    data-testid="contact-centre"
                  >
                    <option value="">{t.contact.centre_any}</option>
                    {centres.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t.contact.message} *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t.contact.message}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4DB5]/30 focus:border-[#2E4DB5] transition-colors resize-none"
                    data-testid="contact-message"
                  />
                </div>
                {submitState === "error" && (
                  <p className="text-red-500 text-sm" data-testid="contact-error">{t.contact.error}</p>
                )}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy-consent"
                    required
                    className="mt-1 w-4 h-4 accent-[#2E4DB5] flex-shrink-0 cursor-pointer"
                    data-testid="privacy-checkbox"
                  />
                  <label htmlFor="privacy-consent" className="text-sm text-slate-600 cursor-pointer">
                    J'ai lu et j'accepte la{" "}
                    <Link to="/confidentialite" className="text-[#2E4DB5] underline hover:opacity-75" target="_blank">
                      Politique de confidentialité
                    </Link>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={submitState === "sending"}
                  className="w-full gradient-bg text-white font-semibold py-4 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitState === "sending" ? t.contact.sending : t.contact.send}
                </button>
              </form>
            )}
          </div>

          {/* Two centres cards */}
          <div className="reveal reveal-delay-2 space-y-4">
            {centres.map((c) => (
              <div key={c.slug} className="bg-slate-50 rounded-3xl p-6" data-testid={`contact-centre-${c.slug}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-outfit font-bold text-slate-900 text-lg leading-tight">{c.name}</h3>
                    <p className="text-slate-500 text-xs mt-0.5">{c.city}</p>
                  </div>
                  <Link
                    to={`/centres/${c.slug}`}
                    className="text-xs font-semibold text-[#2E4DB5] hover:opacity-70 whitespace-nowrap inline-flex items-center gap-1"
                  >
                    {t.centres.discover} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2.5 text-slate-700">
                    <MapPin className="w-4 h-4 text-[#2E4DB5] mt-0.5 flex-shrink-0" />
                    <span>{c.address_line}, {c.postal_code} {c.city}</span>
                  </div>
                  {c.phone && (
                    <div className="flex items-center gap-2.5 text-slate-700">
                      <Phone className="w-4 h-4 text-[#2E4DB5] flex-shrink-0" />
                      <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="hover:text-[#2E4DB5]">{c.phone}</a>
                    </div>
                  )}
                  {c.email && (
                    <div className="flex items-center gap-2.5 text-slate-700">
                      <Mail className="w-4 h-4 text-[#2E4DB5] flex-shrink-0" />
                      <a href={`mailto:${c.email}`} className="hover:text-[#2E4DB5] break-all">{c.email}</a>
                    </div>
                  )}
                </div>
                <a
                  href={c.map_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${c.address_line}, ${c.postal_code} ${c.city}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 gradient-bg text-white text-xs font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                >
                  {t.contact.get_directions} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}

            <Link
              to="/collaborez"
              className="block bg-gradient-to-r from-[#2E4DB5] to-[#3B9DA5] rounded-3xl p-6 text-white hover:opacity-95 transition-opacity group"
              data-testid="collaborer-cta"
            >
              <p className="font-semibold mb-1 flex items-center justify-between">
                {t.news.professional}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </p>
              <p className="text-white/80 text-sm mt-2">{t.collaborer.cta_body}</p>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
